import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Switch,
  Text,
  Textarea
} from '@chakra-ui/react'
import { Dispatch, SetStateAction } from 'react'

import { ActionButton } from '../../../../components/ActionButton'
import { FormField } from '../../../../components/FormField'
import { MoneyInput } from '../../../../components/MaskedInput'
import { SectionTitle } from '../../../../components/SectionCard'
import { SearchableSelect } from '../../../../components/Select'
import { Tooltip } from '../../../../components/Tooltip'
import { ParkingTicketType, TicketType } from '../../../../services/Event/types'
import { DoorSaleTicketFields } from '../DoorSaleTicketFields'
import { IEventForm, ITicketForm } from './eventFormShared'
import { parkingTicketLabel } from './parkingTicket'

const PARKING_HELPER_TEXT =
  'Com um ticket escolhido, a receita de estacionamento do evento passa a vir do ABRA Park ' +
  'automaticamente e deixa de ser editável à mão. Aparecem todos os tickets atípicos, inclusive ' +
  'os inativos ou fora da vigência, marcados como tal.'

interface IProps {
  form: IEventForm
  setForm: Dispatch<SetStateAction<IEventForm>>
  fieldErrors: Record<string, string[]>
  saving: boolean
  addTicket: (type: TicketType) => void
  updateTicket: (index: number, field: keyof ITicketForm, value: unknown) => void
  removeTicket: (index: number) => void
  setCaucaoInfoOpen: (open: boolean) => void
  parkingTicketTypes: ParkingTicketType[]
  selectedParkingTicket: ParkingTicketType | null
  parkingTicketsLoading: boolean
  /** O vínculo salvo não está na lista e a lista é real: o ticket foi excluído no ABRA Park. */
  parkingTicketDeleted: boolean
  /** O vínculo salvo não pôde ser resolvido porque o ABRA Park não respondeu. */
  parkingTicketUnverifiable: boolean
  /** Mensagem de erro do campo de estacionamento, montada no modal — a mesma que alimenta o badge da aba. */
  parkingTicketError?: string[]
  /** Aviso de serviço quando não há vínculo pendente. Vai no helperText, não no error. */
  parkingTicketNotice?: string
}

export function TicketsTab({
  form,
  setForm,
  fieldErrors,
  saving,
  addTicket,
  updateTicket,
  removeTicket,
  setCaucaoInfoOpen,
  parkingTicketTypes,
  selectedParkingTicket,
  parkingTicketsLoading,
  parkingTicketDeleted,
  parkingTicketUnverifiable,
  parkingTicketError,
  parkingTicketNotice
}: IProps) {
  // Um `now` só para a lista inteira, em vez do default da função uma vez por opção: assim as
  // opções não discordam entre si na virada de uma vigência no meio do render.
  const now = new Date()

  return (
    <Flex direction="column" gap={5}>
      <Flex gap={3}>
        <Button size="sm" colorScheme="blue" variant="outline" onClick={() => addTicket('PAID')} disabled={saving}>
          + Ingresso Pago
        </Button>
        <Button size="sm" colorScheme="green" variant="outline" onClick={() => addTicket('FREE')} disabled={saving}>
          + Ingresso Gratuito
        </Button>
      </Flex>

      {form.tickets.map((ticket, idx) => (
        <Box key={idx} border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
          <Flex justifyContent="space-between" alignItems="center" mb={3}>
            <Text fontWeight="bold" fontSize="sm">
              {ticket.type === 'FREE' ? '🟢 Ingresso Gratuito' : '🔵 Ingresso Pago'} #{idx + 1}
            </Text>
            <Flex gap={4} alignItems="center">
              {ticket.type === 'PAID' && (
                <Flex alignItems="center" gap={2}>
                  <FormField label="Taxa isenta" mb={0}>
                    <Switch
                      isChecked={ticket.fee_exempt}
                      onChange={(e) => updateTicket(idx, 'fee_exempt', e.target.checked)}
                      isDisabled={saving}
                    />
                  </FormField>
                </Flex>
              )}
              {ticket.type === 'FREE' && ticket.price > 0 && (
                <Text fontSize="xs" color="orange.500" fontWeight="500">
                  Caução reembolsável
                </Text>
              )}
              <Flex alignItems="center" gap={2}>
                <FormField label="Ingresso Infantil" htmlFor={`ticket-${idx}-is_child_ticket`} mb={0}>
                  <Switch
                    id={`ticket-${idx}-is_child_ticket`}
                    isChecked={ticket.is_child_ticket}
                    onChange={(e) => updateTicket(idx, 'is_child_ticket', e.target.checked)}
                    colorScheme="purple"
                    size="sm"
                    isDisabled={saving}
                  />
                </FormField>
              </Flex>
              <Flex alignItems="center" gap={2}>
                <FormField label="Visível" mb={0}>
                  <Switch
                    isChecked={ticket.visible}
                    onChange={(e) => updateTicket(idx, 'visible', e.target.checked)}
                    isDisabled={saving}
                  />
                </FormField>
              </Flex>
              <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeTicket(idx)} disabled={saving}>
                Remover
              </Button>
            </Flex>
          </Flex>

          <Flex direction="column" gap={3}>
            {ticket.is_child_ticket && (
              <Box bg="purple.50" border="1px solid" borderColor="purple.200" borderRadius="md" p={3}>
                <Text fontSize="xs" fontWeight="600" color="purple.700">
                  Ingresso Infantil
                </Text>
                <Text fontSize="xs" color="purple.700" mt={1}>
                  Não usa cartão NFC no check-in e exige validação da equipe na entrada.
                </Text>
              </Box>
            )}
            <Flex gap={3}>
              <FormField
                label="Título do Ingresso"
                error={fieldErrors[`tickets.${idx}.title`]?.[0]}
                isRequired
                flex="2"
              >
                <Input
                  size="sm"
                  value={ticket.title}
                  onChange={(e) => updateTicket(idx, 'title', e.target.value)}
                  disabled={saving}
                />
              </FormField>
              <FormField label="Quantidade" error={fieldErrors[`tickets.${idx}.quantity`]?.[0]} isRequired flex="1">
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  value={ticket.quantity}
                  onChange={(e) => updateTicket(idx, 'quantity', e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={saving}
                />
              </FormField>
              <FormControl flex="1" isInvalid={!!fieldErrors[`tickets.${idx}.price`]}>
                {ticket.type === 'FREE' ? (
                  <Tooltip label="Preencha R$ 0,00 para ingresso totalmente gratuito. Para cobrar caução anti-desistência, preencha um valor ≥ R$ 5,00. O valor será reembolsado ao participante após check-in no evento.">
                    <FormLabel
                      fontSize="sm"
                      textDecoration="underline"
                      textDecorationStyle="dotted"
                      textDecorationColor="gray.400"
                      cursor="help"
                    >
                      Preço
                    </FormLabel>
                  </Tooltip>
                ) : (
                  <FormLabel fontSize="sm">Preço</FormLabel>
                )}
                <MoneyInput
                  size="sm"
                  value={ticket.price}
                  onChange={(price) => updateTicket(idx, 'price', price ?? 0)}
                  isDisabled={saving}
                />
                <FormErrorMessage>{fieldErrors[`tickets.${idx}.price`]?.[0]}</FormErrorMessage>
              </FormControl>
            </Flex>

            <Flex gap={3}>
              <FormField
                label="Início das Vendas"
                error={fieldErrors[`tickets.${idx}.sales_start`]?.[0]}
                isRequired
                flex="1"
              >
                <Input
                  size="sm"
                  type="datetime-local"
                  value={ticket.sales_start}
                  onChange={(e) => updateTicket(idx, 'sales_start', e.target.value)}
                  disabled={saving}
                />
              </FormField>
              <FormField
                label="Término das Vendas"
                error={fieldErrors[`tickets.${idx}.sales_end`]?.[0]}
                isRequired
                flex="1"
              >
                <Input
                  size="sm"
                  type="datetime-local"
                  value={ticket.sales_end}
                  min={ticket.sales_start}
                  onChange={(e) => updateTicket(idx, 'sales_end', e.target.value)}
                  disabled={saving}
                />
              </FormField>
              <FormField
                label="Máximo por compra"
                error={fieldErrors[`tickets.${idx}.max_per_purchase`]?.[0]}
                isRequired
                flex="1"
              >
                <Input
                  size="sm"
                  type="number"
                  min={1}
                  value={ticket.max_per_purchase}
                  onChange={(e) =>
                    updateTicket(idx, 'max_per_purchase', e.target.value === '' ? '' : Number(e.target.value))
                  }
                  disabled={saving}
                />
              </FormField>
            </Flex>

            {ticket.type === 'PAID' && (
              <DoorSaleTicketFields
                idPrefix={`ticket-${idx}`}
                sellAtDoor={ticket.sell_at_door}
                doorPrice={ticket.door_price}
                error={fieldErrors[`tickets.${idx}.door_price`]?.[0]}
                saving={saving}
                onChange={(patch) =>
                  Object.entries(patch).forEach(([field, value]) =>
                    updateTicket(idx, field as keyof ITicketForm, value)
                  )
                }
              />
            )}

            <Box>
              <FormField label="Descrição do Ingresso">
                <Textarea
                  size="sm"
                  value={ticket.description}
                  placeholder="Este ingresso dá direito a..."
                  onChange={(e) => updateTicket(idx, 'description', e.target.value)}
                  disabled={saving}
                  rows={3}
                />
              </FormField>
            </Box>

            {ticket.type === 'FREE' && ticket.price > 0 && (
              <Box bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" p={3}>
                <Flex justifyContent="space-between" alignItems="flex-start" gap={3}>
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color="orange.700">
                      Caução reembolsável — cobrança à vista
                    </Text>
                    <Text fontSize="xs" color="orange.700" mt={1}>
                      Cobrada no checkout (PIX ou cartão em 1x — o parcelamento é desabilitado) e reembolsada ao
                      participante após o check-in no evento.
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    variant="outline"
                    colorScheme="orange"
                    flexShrink={0}
                    onClick={() => setCaucaoInfoOpen(true)}
                  >
                    ⓘ Entenda
                  </Button>
                </Flex>
              </Box>
            )}
          </Flex>
        </Box>
      ))}

      {form.tickets.some((t) => t.type === 'PAID') && (
        <FormControl
          isInvalid={
            !!fieldErrors['min_installment_value'] ||
            (form.min_installment_value !== null && form.min_installment_value < 5)
          }
        >
          <FormLabel htmlFor="min_installment_value">Valor mínimo de parcela</FormLabel>
          <MoneyInput
            id="min_installment_value"
            value={form.min_installment_value !== null ? Number(form.min_installment_value) : null}
            onChange={(min_installment_value) => setForm((prev) => ({ ...prev, min_installment_value }))}
            isDisabled={saving}
          />
          {form.min_installment_value !== null && form.min_installment_value < 5 ? (
            <FormErrorMessage>O valor mínimo de parcela deve ser pelo menos R$ 5,00.</FormErrorMessage>
          ) : (
            fieldErrors['min_installment_value']?.[0] && (
              <FormErrorMessage>{fieldErrors['min_installment_value'][0]}</FormErrorMessage>
            )
          )}
        </FormControl>
      )}

      <Divider />
      <SectionTitle mb={0}>Estacionamento</SectionTitle>

      <FormField
        label="Ticket do ABRA Park"
        error={fieldErrors['parking_ticket_type_id'] ?? parkingTicketError}
        helperText={
          parkingTicketNotice ? (
            <>
              <Text as="span" display="block" color="orange.600" mb={1}>
                {parkingTicketNotice}
              </Text>
              {PARKING_HELPER_TEXT}
            </>
          ) : (
            PARKING_HELPER_TEXT
          )
        }
      >
        <SearchableSelect<number>
          isClearable
          options={parkingTicketTypes.map((ticket) => ({ value: ticket.id, label: parkingTicketLabel(ticket, now) }))}
          value={selectedParkingTicket?.id ?? ''}
          onChange={(ticketId) => setForm({ ...form, parking_ticket_type_id: ticketId === '' ? null : ticketId })}
          // Enquanto a lista não chega, o vínculo salvo ainda não pôde ser resolvido: dizer
          // "nenhum" ali afirma o oposto da verdade num evento que tem ticket associado.
          placeholder={
            parkingTicketsLoading
              ? 'Carregando...'
              : parkingTicketDeleted
                ? `Ticket #${form.parking_ticket_type_id} (não encontrado)`
                : 'Nenhum (valor preenchido manualmente)'
          }
          entity="ticket"
          isLoading={parkingTicketsLoading}
          // Travado enquanto o ticket associado não puder ser resolvido por falta
          // de resposta do ABRA Park: mexer no campo aqui seria escolher sobre uma
          // lista que não é a real. Quando a lista é real e o ticket não está nela,
          // o campo fica livre — é justamente o caso em que o usuário precisa corrigir.
          isDisabled={saving || parkingTicketUnverifiable}
        />
      </FormField>

      {parkingTicketDeleted && (
        // O react-select não desenha o botão de limpar quando o valor já está vazio
        // (é o caso aqui: o ticket excluído não está em `options`), então sem isto o
        // único jeito de seguir a orientação do aviso acima ("limpe o campo") seria
        // ter outro ticket atípico cadastrado para escolher no lugar dele.
        <ActionButton
          action="unlink"
          label="Desvincular ticket do ABRA Park"
          showLabel
          hideTooltip
          type="button"
          alignSelf="flex-start"
          mr={0}
          isDisabled={saving}
          onClick={() => setForm({ ...form, parking_ticket_type_id: null })}
        />
      )}
    </Flex>
  )
}
